import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { describeLoadFailure, jsonOrThrow } from '../apiJson';
import { categoryBrowsePath } from '../categoryPaths';
import { shouldSubmitSearch } from '../searchSubmit';
import {
  acceptDealsBetaDisclaimer,
  DEALS_BETA_DISCLAIMER_INLINE,
  hasAcceptedDealsBetaDisclaimer,
} from '../dealsBetaDisclaimer';
import { HeaderMoreMenu } from './HeaderMoreMenu';
import { CategoryMenuDropdown } from './CategoryMenuDropdown';
import { HeaderShareButton } from './HeaderShareButton';
import { LogoMark } from './LogoMark';
import { SearchField } from './SearchField';
import { ProductCard } from './ProductCard';
import { ProductGridSkeleton } from './ProductCardSkeleton';
import { SiteFooter } from './SiteFooter';
import { PageHead } from './PageHead';
import { DealsDailyEdition } from './DealsDailyEdition';
import { DealsBetaDisclaimerModal } from './DealsBetaDisclaimerModal';
import './SearchResultsPage.css';
import './DealsOfTheDayPage.css';

function productKey(product) {
  const id = product?.id;
  if (typeof id === 'string' && id.trim() !== '') {
    return id.trim();
  }
  if (typeof id === 'number' && Number.isFinite(id)) {
    return String(id);
  }

  const url = typeof product?.url === 'string' ? product.url.trim() : '';
  return url !== '' ? url : 'product';
}

function dealsSectionId(categorySlug) {
  return categorySlug !== '' ? `deals-section-${categorySlug}` : 'deals-section-category';
}

/**
 * @param {string} sectionId
 */
function scrollToDealsSection(sectionId) {
  const el = document.getElementById(sectionId);
  if (!el) {
    return;
  }

  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * @param {Array<{ categorySlug?: string, categoryName?: string, products?: unknown[] }>} groups
 */
function visibleDealCategories(groups) {
  return groups.filter((group) => {
    const products = Array.isArray(group?.products) ? group.products : [];
    return products.length > 0;
  });
}

function categoryNavShortLabel(name) {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  if (trimmed === '') {
    return 'Category';
  }

  const firstWord = trimmed.split(/\s+/)[0]?.replace(/[,;:]+$/u, '') ?? '';
  return firstWord !== '' ? firstWord : trimmed;
}

export function DealsOfTheDayPage() {
  const navigate = useNavigate();
  const [draftQuery, setDraftQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [validFor, setValidFor] = useState('');
  const [dealsByCategory, setDealsByCategory] = useState([]);
  const [browseCategories, setBrowseCategories] = useState([]);
  const [betaAccepted, setBetaAccepted] = useState(() => hasAcceptedDealsBetaDisclaimer());

  function applySearch(query) {
    const trimmed = typeof query === 'string' ? query.trim() : '';
    if (!shouldSubmitSearch(trimmed)) {
      return;
    }
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  }

  function handleAcceptBetaDisclaimer() {
    acceptDealsBetaDisclaimer();
    setBetaAccepted(true);
  }

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setLoadError(null);

    fetch('/api/deals-of-the-day.php')
      .then((res) => jsonOrThrow(res))
      .then((data) => {
        if (cancelled) {
          return;
        }

        const groups = Array.isArray(data?.dealsByCategory) ? data.dealsByCategory : [];
        setDealsByCategory(groups);
        setValidFor(typeof data?.validFor === 'string' ? data.validFor : '');
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        setDealsByCategory([]);
        setValidFor('');
        setLoadError(describeLoadFailure(err));
        setLoading(false);
      });

    fetch('/api/browse-websites.php')
      .then((res) => jsonOrThrow(res))
      .then((data) => {
        if (cancelled) {
          return;
        }
        setBrowseCategories(Array.isArray(data?.categories) ? data.categories : []);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setBrowseCategories([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const totalProducts = dealsByCategory.reduce(
    (sum, group) => sum + (Array.isArray(group?.products) ? group.products.length : 0),
    0,
  );
  const categorySections = visibleDealCategories(dealsByCategory);

  return (
    <div className="results-page">
      <PageHead
        title="Deal of the Day — Shops.mt"
        description="Today's best discounts from Maltese online stores — in-stock deals picked daily across major categories."
        canonicalPath="/deal-of-the-day"
      />
      <header className="results-page__header">
        <div className="results-page__header-inner">
          <div className="results-page__header-left">
            <LogoMark variant="header" />
            <CategoryMenuDropdown
              browseCategories={browseCategories}
              activeShopCategorySlug=""
              disabled={loading}
            />
            <HeaderMoreMenu />
          </div>
          <div className="results-page__header-center">
            <SearchField
              id="deals-search"
              className="results-page__search search-bar"
              value={draftQuery}
              onChange={setDraftQuery}
              onSubmitSearch={applySearch}
              placeholder="Search products…"
              gaSearchLocation="deal_of_the_day"
            />
          </div>
          <nav
            className="results-page__header-nav"
            aria-label="Site sections"
          >
            <HeaderShareButton location="header" />
            <Link className="results-page__nav-link" to="/about">
              About
            </Link>
            <Link className="results-page__nav-link" to="/add-store">
              Add your store
            </Link>
          </nav>
        </div>
      </header>

      <main className="results-page__main deals-page">
        <header className="deals-page__intro results-page__content-width">
          <h1 className="deals-page__title">Deal of the Day</h1>
          <p className="deals-page__lede">
            Malta&apos;s best discounts, hand-picked and refreshed daily.
          </p>
          {betaAccepted && (
            <p className="deals-page__beta-note" role="note">
              <span className="deals-page__beta-badge">BETA</span>
              <span className="deals-page__beta-note-text">
                {DEALS_BETA_DISCLAIMER_INLINE}
              </span>
            </p>
          )}
          {betaAccepted && !loading && loadError === null && totalProducts > 0 && (
            <DealsDailyEdition
              validFor={validFor}
              categoryCount={categorySections.length}
            />
          )}
        </header>

        {!betaAccepted && (
          <DealsBetaDisclaimerModal onAccept={handleAcceptBetaDisclaimer} />
        )}

        {betaAccepted && !loading && loadError === null && categorySections.length > 0 && (
          <nav
            className="deals-page__category-nav results-page__content-width"
            aria-label="Jump to category"
          >
            <p className="deals-page__category-nav-label">Browse by category</p>
            <ul className="deals-page__category-nav-list">
              {categorySections.map((group) => {
                const categorySlug = typeof group?.categorySlug === 'string'
                  ? group.categorySlug
                  : '';
                const categoryName = typeof group?.categoryName === 'string'
                  ? group.categoryName
                  : 'Category';
                const sectionId = dealsSectionId(categorySlug);

                return (
                  <li key={sectionId} className="deals-page__category-nav-item">
                    <button
                      type="button"
                      className="deals-page__category-nav-btn"
                      onClick={() => scrollToDealsSection(sectionId)}
                      aria-label={`Jump to ${categoryName}`}
                    >
                      <span className="deals-page__category-nav-btn-label deals-page__category-nav-btn-label--full">
                        {categoryName}
                      </span>
                      <span
                        className="deals-page__category-nav-btn-label deals-page__category-nav-btn-label--short"
                        aria-hidden="true"
                      >
                        {categoryNavShortLabel(categoryName)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}

        {betaAccepted && loading && (
          <div
            className="results-page__grid-wrap results-page__content-width"
            aria-busy="true"
            aria-label="Loading deals"
          >
            <ProductGridSkeleton count={8} />
          </div>
        )}

        {betaAccepted && !loading && loadError !== null && (
          <p className="deals-page__status deals-page__status--error results-page__content-width" role="alert">
            {loadError}
          </p>
        )}

        {betaAccepted && !loading && loadError === null && totalProducts === 0 && (
          <p className="deals-page__status results-page__content-width">
            No deals are available right now. Check back tomorrow.
          </p>
        )}

        {betaAccepted && !loading && loadError === null && categorySections.map((group) => {
          const products = Array.isArray(group?.products) ? group.products : [];
          if (products.length === 0) {
            return null;
          }

          const categorySlug = typeof group?.categorySlug === 'string' ? group.categorySlug : '';
          const categoryName = typeof group?.categoryName === 'string'
            ? group.categoryName
            : 'Category';
          const sectionId = dealsSectionId(categorySlug);

          return (
            <section
              key={categorySlug || categoryName}
              id={sectionId}
              className="deals-page__section results-page__grid-wrap results-page__content-width"
              aria-labelledby={`deals-${categorySlug || 'category'}-heading`}
            >
              <header className="deals-page__section-header">
                <h2
                  id={`deals-${categorySlug || 'category'}-heading`}
                  className="deals-page__section-heading"
                >
                  {categorySlug !== '' ? (
                    <Link
                      className="deals-page__section-heading-link"
                      to={categoryBrowsePath(categorySlug)}
                    >
                      {categoryName}
                    </Link>
                  ) : (
                    categoryName
                  )}
                </h2>
              </header>
              <ul className="results-page__grid">
                {products.map((product) => (
                  <li key={productKey(product)} className="results-page__cell">
                    <ProductCard
                      product={product}
                      layout="grid"
                      outboundCampaign="deal_of_the_day"
                      discountBadgePlacement="card-top"
                      editionBadge="Featured today"
                      analyticsContext={{
                        source: 'sale',
                        shopCategorySlug: categorySlug || null,
                      }}
                    />
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </main>

      <SiteFooter />
    </div>
  );
}
